import React from 'react'
import PropTypes from 'prop-types'
import Calendar from 'rc-calendar'
import createPicker from 'antd/lib/date-picker/createPicker'
import wrapPicker from 'antd/lib/date-picker/wrapPicker'
import moment from 'moment'
import classNames from 'classnames'

import FormattedMessage from 'components/FormattedMessage'
import Button from 'components/Button'
import Icon from 'components/Icon'
import Layer from 'components/Layer'

import { formatRanges, isDateInPast } from 'helpers/date'

const withOwnCalendarProps = (ownCalendarProps, { shouldOpenInLayer, close, reset, calendarDidMount }) => {
  return (CalendarComponent) => {
    class CalendarWithOwnProps extends React.PureComponent {
      constructor (props) {
        super(props)

        this.calendarComponentRef = null
      }

      close = (shouldReset) => {
        close()

        if (shouldReset) {
          reset()
        }
      }

      renderFooter = () => {
        return (
          <Button
            translationKey="common.confirmSelection"
            type="primary"
            size="large"
            onClick={() => { this.close() }}
          />
        )
      }

      componentDidMount () {
        if (this.calendarComponentRef) {
          calendarDidMount(this.calendarComponentRef.rootInstance)
        }
      }

      render () {
        const {
          forwardedRef,
          ...restProps
        } = this.props

        const calendar = (
          <CalendarComponent
            ref={(ref) => {
              this.calendarComponentRef = ref
              forwardedRef(ref)
            }}
            {...restProps}
            {...ownCalendarProps}
          />
        )

        if (shouldOpenInLayer()) {
          return (
            <Layer
              onClose={() => {
                this.close(true)
              }}
              ignoreScrolling={true}
              footer={this.renderFooter()}
            >
              {calendar}
            </Layer>
          )
        }

        return calendar
      }
    }

    CalendarWithOwnProps.propTypes = {
      forwardedRef: PropTypes.func,
      shouldOpenInLayer: PropTypes.func,
    }

    return React.forwardRef((props, ref) => {
      return <CalendarWithOwnProps {...props} forwardedRef={ref} />
    })
  }
}

class AntDatePickerWrapper extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      forceOpen: undefined,
      open: false,
      calendarHeight: false,
    }

    this.lastDatePickerValue = props.value

    this.wrappedDatePicker = wrapPicker(createPicker(withOwnCalendarProps({
      disableMonthsInPast: props.disablePastDates,
      prevMonthIcon: <Icon name="angle-left" />,
      nextMonthIcon: <Icon name="angle-right" />,
      highlightToday: false,
    }, {
      shouldOpenInLayer: this.shouldOpenInLayer,
      close: this.close,
      reset: this.reset,
      calendarDidMount: this.calendarDidMount,
    })(Calendar)))

    this.renderFooter = this.renderFooter.bind(this)
  }

  reset = () => {
    this.props.onChange(this.lastDatePickerValue)
  }

  close = () => {
    // set open to false to force the datepicker to close and set it back to undefined so it can be opened again by clicking
    this.setState({
      forceOpen: false,
    })

    this.onOpenChange(false)

    window.setTimeout(() => {
      this.setState({
        forceOpen: undefined,
      })
    })
  }

  shouldOpenInLayer = () => {
    return this.props.openInLayer
  }

  renderFooter () {
    const {
      multiple,
    } = this.props

    if (!multiple) { return null }

    return (
      <React.Fragment>
        <div className="ant-calendar__hint">
          <Icon name="info-circle-thin" />
          <FormattedMessage id="datePicker.multipleSelections" />
        </div>
        {!this.props.openInLayer && (
          <Button
            translationKey="common.confirmSelection"
            onClick={() => { this.close() }}
          />
        )}
      </React.Fragment>
    )
  }

  onOpenChange = (status) => {
    const {
      onOpenChange,
      value,
    } = this.props

    if (status) {
      this.lastDatePickerValue = value
    } else {
      this.setState({
        calendarHeight: false,
      })
    }

    onOpenChange(status)

    this.setState({
      open: status,
    })
  }

  calendarDidMount = (calendarRef) => {
    this.props.calendarDidMount(calendarRef)

    if (this.props.openInPlace) {
      requestAnimationFrame(() => (
        this.setState({
          calendarHeight: calendarRef?.getBoundingClientRect().height,
        })
      ))
    }

    if (this.props.scrollWhenOpen) {
      requestAnimationFrame(() =>
        calendarRef?.scrollIntoView({
          behavior: 'smooth',
        }),
      )
    }
  }

  render () {
    const {
      value,
      defaultPickerValue = [moment()],
      defaultValue,
      onChange,
      multiple,
      size,
      disabled,
      disabledDate,
      disablePastDates,
      placeholder,
      className,
      openInPlace,
    } = this.props

    const {
      open,
      forceOpen,
    } = this.state

    const WrappedDatePicker = this.wrappedDatePicker

    const wrapperClassNames = classNames(
      'ant-input-group-wrapper',
      `ant-input-size-${size}`,
      {
        'ant-calendar--is-open': open,
        'ant-calendar--has-footer': multiple,
      },
      className,
    )

    const disabledDateCheckFunction = disabledDate || (disablePastDates ? isDateInPast : undefined)

    const conditionalProps = {}

    if (placeholder) {
      conditionalProps.placeholder = placeholder
    }

    if (disabledDateCheckFunction) {
      conditionalProps.disabledDate = disabledDateCheckFunction
    }

    if (size) {
      conditionalProps.size = size
    }

    return (
      <>
        <WrappedDatePicker
          className={wrapperClassNames}
          getCalendarContainer={(triggerNode) => triggerNode}
          showToday={false}
          format="DD.MM.YYYY"
          renderExtraFooter={this.renderFooter}
          multiple={multiple}
          selectWeekDays={multiple}
          selectMonths={multiple}
          value={value}
          defaultValue={defaultValue}
          defaultPickerValue={defaultPickerValue}
          onChange={onChange}
          onOpenChange={this.onOpenChange}
          formatInput={formatRanges}
          open={forceOpen}
          pickerIcon={(!open || !value) && <Icon name="calendar" className="ant-calendar-picker-icon" />}
          clearIcon={<Icon name="trash-o" className="ant-calendar-picker-clear" />}
          disabled={disabled}
          tabIndex={-1}
          {...conditionalProps}
        />

        {openInPlace && this.state.calendarHeight && (
          <div style={{ height: `${this.state.calendarHeight}px` }}></div>
        )}
      </>
    )
  }
}

AntDatePickerWrapper.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  defaultPickerValue: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  defaultValue: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  openInPlace: PropTypes.bool,
  openInLayer: PropTypes.bool,
  onChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  multiple: PropTypes.bool,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  disablePastDates: PropTypes.bool,
  disabledDate: PropTypes.func,
  placeholder: PropTypes.string,
  calendarDidMount: PropTypes.func,
  className: PropTypes.string,
  scrollWhenOpen: PropTypes.bool,
}

AntDatePickerWrapper.defaultProps = {
  openInLayer: false,
  multiple: true,
  onOpenChange: () => {},
  size: 'default',
  disabled: false,
  disablePastDates: true,
  calendarDidMount: () => {},
}

export default AntDatePickerWrapper
